import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import React, { useState, useEffect, useMemo } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
// Data
import MDButton from "../../components/MDButton";
import MDInput from "../../components/MDInput";
import OrderEdit from "./edits/OrderEdit";
import buildOrderTable from "./data/orderTableData";
import apis from "../../context/api";
import { timeOf } from "../../utils/format";
import NoAccess from "components/NoAccess";
import { canView, canUpdate, getIsAdmin } from "context/permissions";

function Order() {
  const allowView = canView("Order");
  const allowUpdate = canUpdate("Order");
  const allowInvoice = canView("Invoice");
  // Reverting an order back to a quotation is admin-only.
  const allowRevert = getIsAdmin();
  const [orderUpdate, setOrderUpdate] = useState(false);
  const [id, setId] = useState(null);
  const [orderList, setOrderList] = useState([]);
  const [orderSearch, setOrderSearch] = useState("");
  const [revertId, setRevertId] = useState(null);
  const handleCancel = () => {
    setOrderUpdate(false);
  };
  const orderUpdated = () => {
    setOrderUpdate(false);
    loadTableData();
  };
  useEffect(() => {
    loadTableData();
  }, []);
  const editCombo = (id) => {
    setId(id);
    setOrderUpdate(true);
  };

  const downloadInvoice = (oid) => {
    apis
      .sendAndDownloadInvoice(oid)
      .then(({ blob, filename }) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.target = "_blank";
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch((err) => {
        console.error("Invoice download error:", err);
      });
  };

  const handleRevertConfirm = () => {
    apis
      .revertOrderToQuotation(revertId)
      .then(() => {
        setRevertId(null);
        loadTableData();
      })
      .catch((err) => {
        console.error("Revert to quotation error:", err);
        setRevertId(null);
      });
  };

  const loadTableData = async () => {
    try {
      apis
        .getOrders()
        .then((data) => {
          setOrderList(Array.isArray(data) ? data : []);
        })
        .catch((err) => {
          throw new Error("Can not fetch orders");
        });
    } catch (err) {
      console.error("Data loading error:", err);
    }
  };

  // Pending orders first, then newest first within each status.
  const sortByStatusAndUpdated = (a, b) => {
    const statusOrder = (item) => (item.status === "Delivered" ? 1 : 0);
    return statusOrder(a) - statusOrder(b) || timeOf(b.updatedAt) - timeOf(a.updatedAt);
  };

  const orderData = useMemo(() => {
    const q = orderSearch.trim().toLowerCase();
    const filtered = orderList
      .filter((item) => {
        if (!q) return true;
        const fields = [item.customer?.name, item.customer?.email, item.user?.name];
        return fields.some((f) => (f || "").toLowerCase().includes(q));
      })
      .sort(sortByStatusAndUpdated);
    return buildOrderTable(
      filtered,
      (oid) => editCombo(oid),
      (oid) => downloadInvoice(oid),
      (oid) => setRevertId(oid),
      allowView,
      allowUpdate,
      allowInvoice,
      allowRevert
    );
  }, [orderList, orderSearch, allowView, allowUpdate, allowInvoice, allowRevert]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {orderUpdate && <OrderEdit onSuccess={orderUpdated} id={id} onCancel={handleCancel} />}
      {!orderUpdate && !allowView && (
        <MDBox pt={6} pb={6}>
          <Grid container spacing={6}>
            <Grid item xs={12}>
              <NoAccess message="You do not have permission to view Orders." />
            </Grid>
          </Grid>
        </MDBox>
      )}
      {!orderUpdate && allowView && (
        <MDBox pt={6} pb={6}>
          <Grid container spacing={6}>
            <Grid item xs={12}>
              <Card>
                <MDBox
                  mx={2}
                  mt={-3}
                  py={3}
                  px={2}
                  variant="gradient"
                  bgColor="info"
                  borderRadius="lg"
                  coloredShadow="info"
                >
                  <MDTypography variant="h6" color="white">
                    Orders Table
                  </MDTypography>
                </MDBox>
                <Grid
                  container
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={2}
                  px={2}
                  pt={2}
                >
                  <Grid item xs={12} sm={6} md={4}>
                    <MDInput
                      fullWidth
                      label="Search by customer or quoted by"
                      value={orderSearch}
                      onChange={(e) => setOrderSearch(e.target.value)}
                    />
                  </Grid>
                  {allowUpdate && (
                    <Grid item>
                      <MDButton
                        variant="gradient"
                        color="info"
                        onClick={() => setOrderUpdate(true)}
                      >
                        Update
                      </MDButton>
                    </Grid>
                  )}
                </Grid>
                <MDBox pt={3}>
                  <DataTable
                    table={{ columns: orderData.columns, rows: orderData.rows }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                  />
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
      )}
      <Dialog open={revertId !== null} onClose={() => setRevertId(null)}>
        <DialogTitle>Revert to Quotation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to move this order back to a quotation? It will be removed from
            the Orders list and appear under Quotations.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setRevertId(null)} color="info">
            Cancel
          </MDButton>
          <MDButton onClick={handleRevertConfirm} color="warning" variant="contained">
            Revert
          </MDButton>
        </DialogActions>
      </Dialog>
      <Footer />
    </DashboardLayout>
  );
}

export default Order;
